<template>
    <div class="row process-main">
        <div class="col-xs-12">
            <div class="box box-primary">
                <div class="box-header with-border">
                    <div class="box-header__inner">
                        <h3 class="box-title">{{ $t('unblacklist.processes') }}</h3>

                        <button
                            :data-content="$t('unblacklist.description')"
                            :data-html="true"
                            class="btn btn-sm btn-primary btn-icon btn-round ml5"
                            data-placement="top"
                            data-toggle="popover"
                            data-trigger="focus"
                            type="button"
                        >
                            <i class="fa fa-info"/>
                        </button>

                        <div class="box-tools pull-right">
                            <button
                                :title="$t('unblacklist.newUnblacklist')"
                                class="btn btn-sm btn-primary mr10"
                                data-target="#process-modal"
                                data-toggle="modal"
                                type="button"
                            >
                                {{ $t('unblacklist.newUnblacklist') }}
                            </button>

                            <button
                                :title="$t('collapse')"
                                class="btn btn-box-tool"
                                type="button"
                                @click="isCollapseActive = !isCollapseActive"
                            >
                                <i
                                    :class="{ 'fa-plus': !isCollapseActive, 'fa-minus': isCollapseActive }"
                                    class="fa"
                                />
                            </button>
                        </div>
                    </div>

                    <vue-slide-up-down
                        :active="isUploading"
                        class="progress-block"
                    >
                        <div
                            :class="{
                                'progress-primary': uploadingProgressValue < 100,
                                'progress-success': uploadingProgressValue >= 100,
                            }"
                            class="progress"
                        >
                            <div
                                :aria-valuenow="uploadingProgressValue"
                                :class="{
                                    'progress-bar-primary': uploadingProgressValue < 100,
                                    'progress-bar-success': uploadingProgressValue >= 100,
                                }"
                                :style="{ width: `${uploadingProgressValue}%` }"
                                aria-valuemax="100"
                                aria-valuemin="0"
                                class="progress-bar progress-bar-striped active"
                                role="progressbar"
                            >
                                <div class="progress-bar-value">{{ uploadingProgressText }}</div>
                            </div>
                        </div>
                    </vue-slide-up-down>
                </div>

                <vue-slide-up-down :active="isCollapseActive">
                    <div
                        v-if="processesData.length"
                        class="box-body"
                    >
                        <table class="table">
                            <thead>
                            <tr>
                                <th width="40px">{{ $t('id') }}</th>

                                <th width="120px">{{ $t('status') }}</th>

                                <th width="100px">
                                    {{ $t('unblacklist.date_before') }}
                                </th>

                                <th width="140px">{{ $t('author') }}</th>

                                <th width="180px">{{ $t('startAt') }}</th>

                                <th width="auto">{{ $t('endAt') }}</th>

                                <th width="160px"></th>
                            </tr>
                            </thead>
                            <tbody>

                            <tr
                                v-for="process in processesData"
                                :key="process.id"
                                :class="{ 'is-show': process.isShow }"
                                class="process-row"
                            >
                                <td>
                                    <div>{{ process.id }}</div>
                                </td>

                                <td>
                                    <div>
                                        <button
                                            v-if="process.status === PROCESS_STATUS_ERROR"
                                            :data-content="process.error ? `<b>${$t('error')}:</b> ${process.error}` : undefined"
                                            :data-html="true"
                                            class="btn btn-sm btn-danger label process-label"
                                            data-placement="bottom"
                                            data-toggle="popover"
                                            data-trigger="hover"
                                            type="button"
                                        >
                                            {{ process.statusTitle }}
                                        </button>

                                        <span
                                            v-else
                                            :class="{
                                                    'bg-primary': process.status === PROCESS_STATUS_PROCESSING,
                                                    'bg-yellow': process.status === PROCESS_STATUS_CANCELED,
                                                    'bg-green': process.status === PROCESS_STATUS_FINISHED,
                                                }"
                                            :title="process.statusTitle"
                                            class="label process-label"
                                        >
                                                {{ process.statusTitle }}
                                            </span>
                                    </div>
                                </td>

                                <td>
                                    <div>{{ process.dateBefore }}</div>
                                </td>

                                <td>
                                    <div>{{ process.user ? process.user.name : $t('none') }}</div>
                                </td>

                                <td>
                                    <div>{{ formatDate(process.createdAt) }}</div>
                                </td>

                                <td>
                                    <div>
                                        <div
                                            v-if="process.status === PROCESS_STATUS_PROCESSING"
                                            class="progress progress-primary"
                                        >
                                            <div
                                                :aria-valuenow="process.progressValue"
                                                :style="{ width: `${process.progressValue}%` }"
                                                aria-valuemax="100"
                                                aria-valuemin="0"
                                                class="progress-bar progress-bar-primary progress-bar-striped active"
                                                role="progressbar"
                                            >
                                                <div class="progress-bar-value">{{ `${process.progressValue}%` }}</div>
                                            </div>
                                        </div>

                                        <span v-else>{{ formatDate(process.endAt) }}</span>
                                    </div>
                                </td>

                                <td class="text-right text-nowrap">
                                    <div>
                                        <button
                                            v-if="process.status === PROCESS_STATUS_CANCELED"
                                            :disabled="isLoading"
                                            :title="$t('start')"
                                            class="btn btn-sm btn-warning"
                                            type="button"
                                            @click="$emit('startProcess', { ...process })"
                                        >
                                            <i class="fa fa-play"/>
                                            <span class="sr-only">
                                                    {{ $t('start') }}
                                                </span>
                                        </button>

                                        <a
                                            v-if="process.status === PROCESS_STATUS_FINISHED && process.filename"
                                            :href="`/additional/unblacklist/download/export-file/${process.id}`"
                                            :title="$t('download')"
                                            class="btn btn-sm btn-success"
                                        >
                                            <i class="fa fa-download"/>
                                            <span class="sr-only">{{ $t('download') }}</span>
                                        </a>

                                        <button
                                            v-if="process.status === PROCESS_STATUS_PROCESSING"
                                            :title="$t('cancel')"
                                            class="btn btn-sm btn-danger"
                                            data-target="#confirm-modal"
                                            data-toggle="modal"
                                            type="button"
                                            @click="$emit('confirmProcess', { ...process })"
                                        >
                                            <i class="fa fa-ban"/>
                                            <span class="sr-only">
                                                    {{ $t('cancel') }}
                                                </span>
                                        </button>

                                        <button
                                            v-if="isDeleteEnable(process)"
                                            :title="$t('delete')"
                                            class="btn btn-sm btn-danger"
                                            data-target="#confirm-modal"
                                            data-toggle="modal"
                                            type="button"
                                            @click="$emit('confirmProcess', { ...process })"
                                        >
                                            <i class="fa fa-trash"/>
                                            <span class="sr-only">
                                                    {{ $t('delete') }}
                                                </span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        v-else
                        class="box-body with-border clearfix"
                    >
                        <p class="mb0">
                            {{ $t('messages.unblacklist.noProcesses') }}
                        </p>
                    </div>
                </vue-slide-up-down>
            </div>
        </div>
    </div>
</template>

<script>
import formatsMixin from '@/mixins/formatsMixin';
import processMixin from '@/mixins/processMixin';

export default {
    name: 'UnblacklistProcesses',
    mixins: [formatsMixin, processMixin],
    props: {
        processes: {
            type: Array,
            default: () => [],
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isUploading: {
            type: Boolean,
            default: false,
        },
        uploadingProgressValue: {
            type: Number,
            default: 0,
        },
        uploadingProgressText: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isCollapseActive: true,
        };
    },
    computed: {
        processesData() {
            return this.processes.map((process) => {

                if (process.currentCount === 0 && process.totalCount === 0) {
                    process.progressValue = 0.0;
                } else {
                    process.progressValue = ((process.currentCount / process.totalCount) * 100).toFixed(2);
                }

                switch (process.status) {
                    case this.PROCESS_STATUS_PROCESSING:
                        process.statusTitle = this.$t('processing');
                        break;
                    case this.PROCESS_STATUS_CANCELED:
                        process.statusTitle = this.$t('canceled');
                        break;
                    case this.PROCESS_STATUS_FINISHED:
                        process.statusTitle = this.$t('finished');
                        break;
                    case this.PROCESS_STATUS_ERROR:
                        process.statusTitle = this.$t('error');
                        break;
                }

                return process;
            });
        }
    },
    watch: {
        isUploading(value) {
            if (value) {
                this.isCollapseActive = true;
            }
        },
        processes: {
            handler(processes) {
                processes.forEach((process) => {
                    if (process.error) {
                        $('[data-toggle="popover"]').popover();
                    }
                });
            },
            deep: true,
        },
    },
    methods: {
        async createProcess({event}) {
            await this.$emit('createProcess', {event});

            if (event) {
                event.target.value = '';
            }
        },
        isDeleteEnable(process) {
            return !!(
                process.status === this.PROCESS_STATUS_FINISHED ||
                process.status === this.PROCESS_STATUS_CANCELED ||
                process.status === this.PROCESS_STATUS_ERROR
            );
        },
    },
};
</script>

<style src="./UnblacklistProcesses.css"/>
